/**
 * react-hook-form integration with the shared validation schemas. The same schema
 * validates on the client (instant feedback) and the server (authoritative); server
 * field errors are mapped back onto the form.
 */
import type { Schema, FieldErrors as SchemaErrors } from "@shared/validation.ts";
import { useState } from "react";
import { type DefaultValues, type FieldValues, type Resolver, useForm } from "react-hook-form";
import { ApiError } from "./api.ts";

function setIn(target: Record<string, unknown>, path: string, value: unknown) {
  const keys = path.split(".");
  let node = target;
  keys.forEach((key, i) => {
    if (i === keys.length - 1) {
      node[key] = value;
      return;
    }
    const nextIsIndex = /^\d+$/.test(keys[i + 1]!);
    node[key] ??= nextIsIndex ? [] : {};
    node = node[key] as Record<string, unknown>;
  });
}

export function getIn(source: unknown, path: string): unknown {
  return path
    .split(".")
    .reduce<unknown>(
      (node, key) => (node == null ? undefined : (node as Record<string, unknown>)[key]),
      source,
    );
}

export type Check<T> = (value: T) => SchemaErrors | null;

export function schemaResolver<T>(schema: Schema<T>, checks: Check<T>[] = []): Resolver<FieldValues> {
  return async (values) => {
    const result = schema.parse(values);
    let errors: SchemaErrors = result.ok ? {} : result.errors;
    if (result.ok) for (const check of checks) errors = { ...errors, ...(check(result.value) ?? {}) };
    if (Object.keys(errors).length === 0 && result.ok)
      return { values: result.value as FieldValues, errors: {} };
    const nested: Record<string, unknown> = {};
    for (const [path, message] of Object.entries(errors))
      setIn(nested, path === "_" ? "root" : path, { type: "validate", message });
    return { values: {}, errors: nested as never };
  };
}

/**
 * A form bound to a shared schema. `submit(fn)` handles loading state, maps server
 * validation errors to fields and exposes a form-level error message.
 */
export function useSchemaForm<T>(
  schema: Schema<T>,
  defaultValues: Record<string, unknown>,
  checks: Check<T>[] = [],
) {
  const form = useForm<FieldValues>({
    defaultValues: defaultValues as DefaultValues<FieldValues>,
    resolver: schemaResolver(schema, checks),
    mode: "onTouched",
  });
  const [formError, setFormError] = useState<string | null>(null);

  const submit = (fn: (value: T) => Promise<unknown>) =>
    form.handleSubmit(async (value) => {
      setFormError(null);
      try {
        await fn(value as T);
      } catch (e) {
        if (e instanceof ApiError) {
          for (const [field, message] of Object.entries(e.fields))
            form.setError(field as never, { type: "server", message });
          setFormError(e.message);
        } else setFormError(e instanceof Error ? e.message : String(e));
      }
    });

  return { form, formError, setFormError, submit };
}
